import React, { useState, useRef, useEffect } from 'react';
import { MicrophoneIcon, StopIcon, TrashIcon } from '@heroicons/react/24/solid';
import { PlayIcon, PauseIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';

interface AudioRecorderProps {
  onAudioRecorded: (audioBlob: Blob) => void;
  onCancel: () => void;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({ onAudioRecorded, onCancel }) => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Iniciar grabación
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Configurar el MediaRecorder con opciones específicas para asegurar compatibilidad
      const options = { mimeType: 'audio/webm;codecs=opus' };
      let mediaRecorder;

      // Verificar si el navegador soporta el tipo MIME especificado
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mediaRecorder = new MediaRecorder(stream, options);
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      } else {
        // Usar configuración predeterminada si no se soportan los tipos anteriores
        mediaRecorder = new MediaRecorder(stream);
      }

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Configurar para capturar datos cada 1 segundo en lugar de solo al final
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          console.log('Datos de audio capturados:', event.data.size, 'bytes');
        }
      };

      mediaRecorder.onstop = () => {
        console.log('Grabación detenida. Chunks capturados:', audioChunksRef.current.length);

        if (audioChunksRef.current.length === 0) {
          console.error('No se capturaron datos de audio');
          alert('No se pudo grabar audio. Por favor, intenta de nuevo.');
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        console.log('Blob de audio creado:', audioBlob.size, 'bytes');
        setAudioBlob(audioBlob);

        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        // Detener todos los tracks del stream
        stream.getTracks().forEach(track => track.stop());
      };

      // Iniciar grabación con timeslice de 1000ms para obtener datos periódicamente
      mediaRecorder.start(1000);
      setIsRecording(true);

      // Iniciar temporizador
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      console.log('Grabación iniciada correctamente');

    } catch (error) {
      console.error('Error al acceder al micrófono:', error);
      alert('No se pudo acceder al micrófono. Por favor, verifica los permisos.');
    }
  };

  // Detener grabación
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        // Asegurarse de que se capturen los últimos datos antes de detener
        if (mediaRecorderRef.current.state === 'recording') {
          // Forzar un evento dataavailable antes de detener
          mediaRecorderRef.current.requestData();

          // Esperar un momento para asegurar que los datos se procesen
          setTimeout(() => {
            mediaRecorderRef.current?.stop();
            console.log('Grabación detenida manualmente');
          }, 100);
        } else {
          mediaRecorderRef.current.stop();
        }

        setIsRecording(false);

        // Detener temporizador
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      } catch (error) {
        console.error('Error al detener la grabación:', error);
        // Intentar limpiar recursos en caso de error
        setIsRecording(false);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    }
  };

  // Reproducir audio grabado
  const playAudio = () => {
    if (audioRef.current && audioUrl) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  // Pausar reproducción
  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  // Enviar audio grabado
  const sendAudio = () => {
    if (audioBlob) {
      onAudioRecorded(audioBlob);
    }
  };

  // Cancelar grabación
  const cancelRecording = () => {
    try {
      // Detener la grabación si está en curso
      if (isRecording && mediaRecorderRef.current) {
        // Intentar detener la grabación de forma segura
        if (mediaRecorderRef.current.state === 'recording') {
          try {
            mediaRecorderRef.current.stop();
            console.log('Grabación cancelada');
          } catch (e) {
            console.error('Error al detener el mediaRecorder:', e);
          }
        }

        // Detener y liberar el stream de audio
        try {
          const tracks = mediaRecorderRef.current.stream?.getTracks() || [];
          tracks.forEach(track => track.stop());
          console.log('Tracks de audio detenidos:', tracks.length);
        } catch (e) {
          console.error('Error al detener tracks de audio:', e);
        }

        setIsRecording(false);
      }

      // Detener el temporizador
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Liberar URL del objeto si existe
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }

      // Reiniciar el estado
      setAudioBlob(null);
      setAudioUrl(null);
      setRecordingTime(0);
      audioChunksRef.current = [];

      // Notificar al componente padre
      onCancel();

    } catch (error) {
      console.error('Error al cancelar la grabación:', error);
      // Asegurar que se notifique al componente padre incluso en caso de error
      onCancel();
    }
  };

  // Formatear tiempo de grabación
  const formatTime = (seconds: number) => {
    if (seconds === undefined || seconds === null) {
      return '00:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Limpiar al desmontar el componente
  useEffect(() => {
    // Guardar referencias para la limpieza
    const timerRefCurrent = timerRef.current;
    const mediaRecorderRefCurrent = mediaRecorderRef.current;

    return () => {
      // Detener temporizador si existe
      if (timerRefCurrent) {
        clearInterval(timerRefCurrent);
      }

      // Liberar URL del objeto si existe
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }

      // Detener grabación si está en curso
      if (mediaRecorderRefCurrent && mediaRecorderRefCurrent.state === 'recording') {
        try {
          mediaRecorderRefCurrent.stop();
        } catch (e) {
          console.error('Error stopping recorder on cleanup:', e);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar en mount/unmount

  // Manejar evento de finalización de reproducción
  useEffect(() => {
    const audioElement = audioRef.current;

    const handleEnded = () => {
      setIsPlaying(false);
    };

    if (audioElement) {
      audioElement.addEventListener('ended', handleEnded);
    }

    return () => {
      if (audioElement) {
        audioElement.removeEventListener('ended', handleEnded);
      }
    };
  }, []);

  // Monitorear el temporizador de grabación
  useEffect(() => {
    if (isRecording) {
      console.log('Tiempo de grabación actualizado:', recordingTime);
    }
  }, [recordingTime, isRecording]);

  return (
    <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
      {/* Audio element (hidden) */}
      <audio ref={audioRef} src={audioUrl || ''} />
      {/* Grabación en progreso */}
      {isRecording && (
        <div className="flex flex-col items-center w-full">
          <div className="text-center mb-3 w-full">
            <div className="text-lg font-medium text-domired-600 dark:text-domired-500">{t('messages.recordVoice')}</div>

            {/* Cronómetro más visible */}
            <div className="flex items-center justify-center mt-2 bg-gray-200 dark:bg-gray-700 rounded-full px-4 py-2 shadow-inner">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-3 animate-pulse"></div>
              <div className="text-gray-800 dark:text-gray-100 font-mono text-2xl font-bold" data-testid="recording-timer">
                {formatTime(recordingTime)}
              </div>
            </div>
          </div>

          <div className="flex space-x-6 mt-3 mb-2 w-full justify-center">
            <button
              onClick={stopRecording}
              className="bg-domired-600 text-white p-4 rounded-full hover:bg-domired-700 transition-colors focus:outline-none focus:ring-2 focus:ring-domired-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 shadow-md"
              aria-label={t('messages.stopRecording')}
            >
              <StopIcon className="h-6 w-6" />
            </button>
            <button
              onClick={cancelRecording}
              className="bg-gray-500 text-white p-4 rounded-full hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800 shadow-md"
              aria-label={t('messages.cancelRecording')}
            >
              <TrashIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}

      {/* Previsualización de audio grabado */}
      {!isRecording && audioBlob && (
        <div className="flex flex-col items-center w-full">
          <div className="text-center mb-3 w-full">
            <div className="text-lg font-medium text-domired-600 dark:text-domired-500">{t('messages.recordedMessage')}</div>
            <div className="flex items-center justify-center mt-1">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <div className="text-gray-700 dark:text-gray-300 font-mono text-xl font-bold">{formatTime(recordingTime)}</div>
            </div>
          </div>

          <div className="w-full max-w-xs bg-gray-200 dark:bg-gray-700 h-2 rounded-full mb-4">
            <div
              className="bg-domired-500 h-2 rounded-full"
              style={{ width: '100%' }}
            ></div>
          </div>

          <div className="flex space-x-3 mb-4">
            {!isPlaying ? (
              <button
                onClick={playAudio}
                className="bg-domired-600 text-white p-3 rounded-full hover:bg-domired-700 transition-colors focus:outline-none focus:ring-2 focus:ring-domired-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                aria-label={t('messages.playAudio')}
              >
                <PlayIcon className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={pauseAudio}
                className="bg-domired-600 text-white p-3 rounded-full hover:bg-domired-700 transition-colors focus:outline-none focus:ring-2 focus:ring-domired-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                aria-label={t('messages.pauseAudio')}
              >
                <PauseIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          <div className="flex space-x-4 w-full justify-center">
            <button
              onClick={sendAudio}
              className="bg-domired-600 text-white px-6 py-2 rounded-lg hover:bg-domired-700 transition-colors focus:outline-none focus:ring-2 focus:ring-domired-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 font-medium"
            >
              {t('messages.sendVoice')}
            </button>

            <button
              onClick={cancelRecording}
              className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800 font-medium"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Botón para iniciar grabación */}
      {!isRecording && !audioBlob && (
        <div className="flex flex-col items-center w-full">
          <div className="text-center mb-4">
            <div className="text-lg font-medium text-domired-600 dark:text-domired-500">{t('messages.recordVoice')}</div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('messages.recordingInstructions')}</p>
          </div>

          <div className="flex justify-center w-full">
            <button
              onClick={startRecording}
              className="bg-domired-600 text-white p-4 rounded-full hover:bg-domired-700 transition-colors focus:outline-none focus:ring-2 focus:ring-domired-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
              aria-label="Iniciar grabación"
            >
              <MicrophoneIcon className="h-8 w-8" />
            </button>
          </div>

          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            {t('messages.allowMicrophoneAccess')}
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioRecorder;
